// services/finnhubAPI.js — Stock market API client (via Worker proxy)
// Stocks: Italian (.MI) → Yahoo, US → Finnhub/TwelveData.
// Uses shared workerClient for fetch + cache.

import { trackFinnhub, trackTwelveData, trackYahoo } from './apiMonitor';
import { cachedFetch, fetchMultiple, clearCache as clearAll } from './workerClient';

// ── Cache TTLs (ms) ─────────────────────────────────────────
const TTL = { quote: 30000, candles: 600000, profile: 86400000, news: 300000, search: 60000 };

// ── Tracking helpers ────────────────────────────────────────
function isIT(s) { return s.endsWith('.MI'); }
function trackQuote(s) { return (fromCache) => isIT(s) ? trackYahoo(fromCache) : trackFinnhub(fromCache); }
function trackCandle(s) { return (fromCache) => isIT(s) ? trackYahoo(fromCache) : trackTwelveData(fromCache); }

// ── Exported API functions ──────────────────────────────────

export async function getQuote(s) {
  return cachedFetch('quote_' + s, '/api/quote/' + encodeURIComponent(s), TTL.quote, trackQuote(s));
}

export async function getCompanyProfile(s) {
  return cachedFetch('profile_' + s, '/api/profile/' + encodeURIComponent(s), TTL.profile, trackQuote(s));
}

export async function getCandles(s, _res, days) {
  if (!days) days = 30;
  try {
    return await cachedFetch(
      'candles_' + s + '_' + days,
      '/api/candles/' + encodeURIComponent(s) + '?days=' + days,
      TTL.candles,
      trackCandle(s)
    );
  } catch (e) {
    console.error('[Candles]', e);
    return null;
  }
}

export async function getNews(s, days) {
  if (!days) days = 7;
  const k = 'news_' + s + '_' + days;
  // News needs post-processing (dateRelative), so we use cachedFetch for raw + transform
  try {
    const raw = await cachedFetch(
      k, '/api/news/' + encodeURIComponent(s) + '?days=' + days,
      TTL.news,
      (fromCache) => trackFinnhub(fromCache)
    );
    // Add dateRelative for UI (safe to recompute on cache hit)
    return (raw || []).map(n => ({
      ...n,
      date: new Date(n.date),
      dateRelative: getRelativeTime(new Date(n.date).getTime()),
    }));
  } catch (e) {
    return [];
  }
}

export async function searchSymbol(q) {
  if (!q || q.length < 1) return [];
  return cachedFetch(
    'search_' + q,
    '/api/search?q=' + encodeURIComponent(q),
    TTL.search,
    (fromCache) => trackFinnhub(fromCache)
  );
}

export async function getMultipleQuotes(syms) {
  return fetchMultiple(syms, getQuote);
}

export async function getFullStockData(s) {
  const r = await Promise.allSettled([
    getQuote(s),
    getCompanyProfile(s),
    getCandles(s, 'D', 30),
    getNews(s, 7),
  ]);
  return {
    symbol: s,
    quote: r[0].status === 'fulfilled' ? r[0].value : null,
    profile: r[1].status === 'fulfilled' ? r[1].value : null,
    candles: r[2].status === 'fulfilled' ? r[2].value : null,
    news: r[3].status === 'fulfilled' ? r[3].value : null,
  };
}

// ── Utilities ───────────────────────────────────────────────
function getRelativeTime(ts) {
  const d = Date.now() - ts;
  const m = Math.floor(d / 60000);
  if (m < 60) return m + ' min fa';
  const h = Math.floor(m / 60);
  if (h < 24) return h + ' ore fa';
  return Math.floor(h / 24) + ' giorni fa';
}

export { clearAll as clearCache };
